import { Suspense, lazy } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom';
import { LayoutTestPage } from './pages/LayoutTestPage';
import { useTranslation } from 'react-i18next'
import Layout from "./components/Layout"
import { OrgAuthProvider, useOrgAuth } from "./contexts/OrgAuthContext"
import HomePage from './pages/HomePage'
import LoginPage from './pages/LoginPage'
import NotFoundPage from './pages/NotFoundPage'
import AcceptInvitationPage from './pages/AcceptInvitationPage'
import MagicLinkPage from './pages/MagicLinkPage'
import MoroccanLoader from './components/MoroccanLoader'
import { PWAInstallPrompt } from './components/PWAInstallPrompt'
import ErrorBoundary from './components/ErrorBoundary'
import NetworkStatusIndicator from './components/NetworkStatusIndicator'
import OrgAppShell from './shells/OrgAppShell'
import BookmarksRedirect from './features/procurement/bookmarks/BookmarksRedirect'

// ── Lazy-loaded route components (only fetched when the route is visited) ────

// ── Shells (loaded only when the user navigates to that section) ─────────────
const PlatformAdminShell              = lazy(() => import('./shells/PlatformAdminShell'))
const ProcurementModuleShell          = lazy(() => import('./features/procurement/ProcurementModuleShell'))
const OrganizationModuleShell         = lazy(() => import('./features/organization/OrganizationModuleShell'))
const WebsiteBuilderModuleShell       = lazy(() => import('./features/website-builder/WebsiteBuilderModuleShell'))
const ToolsModuleShell                = lazy(() => import('./features/tools/ToolsModuleShell'))
const ProjectManagementModuleShell    = lazy(() => import('./features/project-management/ProjectManagementModuleShell'))
const ProjectManagementDashboardPage  = lazy(() => import('./features/project-management/pages/DashboardPage'))
const ProjectManagementProjectsPage   = lazy(() => import('./features/project-management/pages/ProjectsListPage'))
const ProjectManagementTaxonomyPage   = lazy(() => import('./features/project-management/pages/TaxonomyPage'))
const GlobalPlanningPage              = lazy(() => import('./features/project-management/pages/GlobalPlanningPage'))
const ProjectShell                    = lazy(() => import('./features/project-management/pages/ProjectShell'))
const TasksTab                        = lazy(() => import('./features/project-management/pages/TasksTab'))
const PlanningTab                     = lazy(() => import('./features/project-management/pages/PlanningTab'))
const MembersTab                      = lazy(() => import('./features/project-management/pages/MembersTab'))
const HistoryTab                      = lazy(() => import('./features/project-management/pages/HistoryTab'))
// ── Pages ─────────────────────────────────────────────────────────────────────
const ProcurementsPage                = lazy(() => import('./pages/ProcurementsPage'))
const AdminLoginPage                  = lazy(() => import('./pages/admin/AdminLoginPage'))
const AdminDashboardPage              = lazy(() => import('./pages/admin/AdminDashboardPage'))
const OrgDashboardPage                = lazy(() => import('./pages/org/OrgDashboardPage'))
const OrgMembersPage                  = lazy(() => import('./pages/org/OrgMembersPage'))
const OrgRolesPage                    = lazy(() => import('./pages/org/OrgRolesPage'))
const OrgRoleCreatePage               = lazy(() => import('./pages/org/OrgRoleCreatePage'))
const OrgRoleEditPage                 = lazy(() => import('./pages/org/OrgRoleEditPage'))
const OrgCompanyDataPage              = lazy(() => import('./pages/org/OrgCompanyDataPage'))
const OrgStoragePage                  = lazy(() => import('./pages/org/OrgStoragePage'))
const CatchUpPage                     = lazy(() => import('./features/procurement/catchup/CatchUpPage'))
const ProcurementFiltersPage          = lazy(() => import('./features/procurement/filters/ProcurementFiltersPage'))
const ProcurementExplorePage          = lazy(() => import('./features/procurement/explore/ProcurementExplorePage'))
const ProcurementDocumentsPage        = lazy(() => import('./features/procurement/ProcurementDocumentsPage'))
const TrackingBoardPage               = lazy(() => import('./features/procurement/tracking/pages/TrackingBoardPage'))
const AnalyticsLayout                 = lazy(() => import('./features/procurement/analytics/AnalyticsLayout'))
const AnalyticsOverviewPage           = lazy(() => import('./features/procurement/analytics/pages/OverviewPage'))
const AnalyticsPricingPage            = lazy(() => import('./features/procurement/analytics/pages/PricingPage'))
const AnalyticsMarketPage             = lazy(() => import('./features/procurement/analytics/pages/MarketPage'))
const AnalyticsFunnelPage             = lazy(() => import('./features/procurement/analytics/pages/FunnelPage'))
const AnalyticsRadarPage              = lazy(() => import('./features/procurement/analytics/pages/RadarPage'))
const NotificationSettingsPage            = lazy(() => import('./pages/account/NotificationSettingsPage'))
const WebsiteBuilderLandingPage       = lazy(() => import('./features/website-builder/WebsiteBuilderLandingPage'))
const WebsiteBrandPage                = lazy(() => import('./features/website-builder/WebsiteBrandPage'))
const WebsiteImagesPage               = lazy(() => import('./features/website-builder/WebsiteImagesPage'))
const WebsitePagesPage                = lazy(() => import('./features/website-builder/WebsitePagesPage'))
const WebsiteEditorPage               = lazy(() => import('./features/website-builder/WebsiteEditorPage'))
const WebsiteAssistantPage            = lazy(() => import('./features/website-builder/assistant/WebsiteAssistantPage'))
const WebsiteNavbarPage               = lazy(() => import('./features/website-builder/WebsiteNavbarPage'))
const WebsiteFooterPage               = lazy(() => import('./features/website-builder/WebsiteFooterPage'))
const WebsitePublishPage              = lazy(() => import('./features/website-builder/WebsitePublishPage'))
const WebsiteDomainsPage              = lazy(() => import('./features/website-builder/WebsiteDomainsPage'))
const WebsiteStoragePage              = lazy(() => import('./features/website-builder/WebsiteStoragePage'))
const WebsiteResetPage                 = lazy(() => import('./features/website-builder/WebsiteResetPage'))
const AiChatModuleShell                = lazy(() => import('./features/ai-chat/AiChatModuleShell'))
const AiChatLandingPage                = lazy(() => import('./features/ai-chat/pages/AiChatLandingPage'))
const AiChatDiscussionsPage            = lazy(() => import('./features/ai-chat/pages/DiscussionsPage'))
const AiChatConversationPage           = lazy(() => import('./features/ai-chat/pages/ConversationDetailPage'))
const AiSkillsAdminPage                = lazy(() => import('./features/ai-chat/pages/SkillsAdminPage'))
const AiGovernancePage                 = lazy(() => import('./features/ai-governance/pages/GovernancePage'))
const AiMyUsagePage                    = lazy(() => import('./features/ai-governance/pages/MyConsumptionPage'))
const LetterheadsModuleShell           = lazy(() => import('./features/letterheads/LetterheadsModuleShell'))
const LetterheadLibraryPage            = lazy(() => import('./features/letterheads/pages/LetterheadLibraryPage'))
const LetterheadEditorPage             = lazy(() => import('./features/letterheads/pages/LetterheadEditorPage'))
const LetterheadLettersPage            = lazy(() => import('./features/letterheads/pages/LetterheadLettersPage'))
const LetterheadLetterViewerPage       = lazy(() => import('./features/letterheads/pages/LetterheadLetterViewerPage'))
const TiersModuleShell                 = lazy(() => import('./features/tiers/TiersModuleShell'))
const PartiesListPage                  = lazy(() => import('./features/tiers/pages/PartiesListPage'))
const PartyDetailPage                  = lazy(() => import('./features/tiers/pages/PartyDetailPage'))
const PartyCreatePage                  = lazy(() => import('./features/tiers/pages/PartyCreatePage'))
const PartyImportPage                  = lazy(() => import('./features/tiers/pages/PartyImportPage'))
const VaultDashboardPage               = lazy(() => import('./features/vault/pages/VaultDashboardPage'))
const VaultDocumentDetailPage          = lazy(() => import('./features/vault/pages/VaultDocumentDetailPage'))
const VaultTypesPage                   = lazy(() => import('./features/vault/pages/VaultTypesPage'))
const VaultModuleShell                 = lazy(() => import('./features/vault/VaultModuleShell'))
const ToolsLandingPage                = lazy(() => import('./features/tools/ToolsLandingPage'))
const DocumentComparisonPage          = lazy(() => import('./features/tools/document-comparison'))
const NumberToWordsPage               = lazy(() => import('./features/tools/NumberToWordsPage'))
const PdfEditorPage                   = lazy(() => import('./features/tools/pdf-editor'))
const ArchiveManagerPage              = lazy(() => import('./features/tools/archive-manager'))
const ArabicKeyboardPage              = lazy(() => import('./features/tools/ArabicKeyboardPage'))

function ModuleGuard({ moduleKey, children }: { moduleKey: string; children: React.ReactNode }) {
  const { session, isLoading } = useOrgAuth()
  if (isLoading) return null
  const activeModules = session?.current_organization?.active_modules ?? []
  if (!activeModules.includes(moduleKey)) return <Navigate to="/app" replace />
  return <>{children}</>
}

function PermissionGuard({ permission, children }: { permission: string; children: React.ReactNode }) {
  const { session, isLoading } = useOrgAuth()
  if (isLoading) return null
  const permissions = session?.permissions ?? []
  if (!permissions.includes(permission)) {
    return <Navigate to="/app" replace />
  }
  return <>{children}</>
}

// Full-page loader for initial app load
function AppLoader() {
  const { t } = useTranslation()
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <MoroccanLoader size="lg" message={t('common.loading')} />
    </div>
  )
}

function App() {
  return (
    <ErrorBoundary>
      <NetworkStatusIndicator />
      <OrgAuthProvider>
        <Suspense fallback={<AppLoader />}>
          <Routes>
            {/* TEST LAYOUT PROTOTYPE */}
            <Route path="/test-layout" element={<LayoutTestPage />} />
            {/* Platform Admin — login is public; everything else is guarded */}
            <Route path="/admin/login" element={<AdminLoginPage />} />
            <Route path="/admin" element={<PlatformAdminShell />}>
              <Route path="dashboard" element={<AdminDashboardPage />} />
              <Route path="issues" element={<AdminDashboardPage />} />
              <Route path="organizations" element={<AdminDashboardPage />} />
              <Route path="organizations/:orgId" element={<AdminDashboardPage />} />
              <Route path="storage" element={<AdminDashboardPage />} />
              <Route path="environment" element={<AdminDashboardPage />} />
              <Route path="metrics" element={<AdminDashboardPage />} />
              <Route path="ai-usage" element={<AdminDashboardPage />} />
              <Route path="ai-metrics" element={<AdminDashboardPage />} />
              <Route path="ai-governance" element={<AdminDashboardPage />} />
              <Route path="ai-conversations" element={<AdminDashboardPage />} />
              <Route path="usage-analytics" element={<AdminDashboardPage />} />
              <Route path="notifications" element={<AdminDashboardPage />} />
              <Route path="broadcast" element={<AdminDashboardPage />} />
              <Route path="execution" element={<AdminDashboardPage />} />
              <Route path="hosts" element={<AdminDashboardPage />} />
              <Route path="procurements/results" element={<AdminDashboardPage />} />
              <Route path="procurements/stale" element={<AdminDashboardPage />} />
              <Route path="downloads/stale" element={<AdminDashboardPage />} />
              <Route path="procurements/results-eligible" element={<AdminDashboardPage />} />
              <Route path="procurements/missing-files" element={<AdminDashboardPage />} />
              <Route path="procurements/missing-queue" element={<AdminDashboardPage />} />
              <Route path="procurements/by-date" element={<AdminDashboardPage />} />
              <Route path="procurements/notifications" element={<AdminDashboardPage />} />
              <Route path="procurements/saved-filters" element={<AdminDashboardPage />} />
              <Route path="project-management/notifications" element={<AdminDashboardPage />} />
              <Route path="analytics/results" element={<AdminDashboardPage />} />
              <Route path="analytics/stale" element={<AdminDashboardPage />} />
              <Route path="database/migrations" element={<AdminDashboardPage />} />
              <Route path="database/import-export" element={<AdminDashboardPage />} />
              <Route path="database/integrity" element={<AdminDashboardPage />} />
              <Route path="docling/web-client" element={<AdminDashboardPage />} />
              <Route path="docling/stats" element={<AdminDashboardPage />} />
            </Route>

            {/* Org App Shell */}
            <Route path="/app" element={<OrgAppShell />}>
              <Route index element={<OrgDashboardPage />} />
              <Route path="settings/notifications" element={<NotificationSettingsPage />} />
              <Route path="organization" element={<OrganizationModuleShell />}>
                {/* The org landing never earned its keep — Members is the 90% case (locked landing map) */}
                <Route index element={<Navigate replace to="members" />} />
                <Route path="members" element={<OrgMembersPage />} />
                <Route path="roles" element={<OrgRolesPage />} />
                <Route path="roles/new" element={<OrgRoleCreatePage />} />
                <Route path="roles/:roleId/edit" element={<OrgRoleEditPage />} />
                <Route path="company-data" element={<OrgCompanyDataPage />} />
                <Route path="storage" element={<OrgStoragePage />} />
              </Route>
              <Route
                path="website"
                element={
                  <ModuleGuard moduleKey="website_builder">
                    <PermissionGuard permission="module.website.view">
                      <WebsiteBuilderModuleShell />
                    </PermissionGuard>
                  </ModuleGuard>
                }
              >
                <Route index element={<WebsiteBuilderLandingPage />} />
                <Route path="brand" element={<WebsiteBrandPage />} />
                <Route path="images" element={<WebsiteImagesPage />} />
                <Route path="pages" element={<WebsitePagesPage />} />
                <Route path="editor/:pageId" element={<WebsiteEditorPage />} />
                {/* The AI mode (chat plan §3.1): surfaced in the module nav, and gated on
                    the ai_chat view permission the conversation endpoints enforce (D6). */}
                <Route
                  path="assistant"
                  element={
                    <PermissionGuard permission="module.ai_chat.view">
                      <WebsiteAssistantPage />
                    </PermissionGuard>
                  }
                />
                <Route path="navbar" element={<WebsiteNavbarPage />} />
                <Route path="footer" element={<WebsiteFooterPage />} />
                <Route path="publish" element={<WebsitePublishPage />} />
                {/* Org self-serve custom domains — the endpoints enforce this
                    permission too (module.website.domains). */}
                <Route
                  path="domains"
                  element={
                    <PermissionGuard permission="module.website.domains">
                      <WebsiteDomainsPage />
                    </PermissionGuard>
                  }
                />
                <Route path="storage" element={<WebsiteStoragePage />} />
                <Route path="reset" element={<WebsiteResetPage />} />
              </Route>
              <Route path="procurement" element={<PermissionGuard permission="module.procurements.view"><ProcurementModuleShell /></PermissionGuard>}>
                <Route index element={<CatchUpPage />} />
                <Route path="filters" element={<ProcurementFiltersPage />} />
                <Route path="explore" element={<ProcurementExplorePage />} />
                <Route path="explore/:refConsultationId/documents" element={<ProcurementDocumentsPage />} />
                <Route path="bookmarks" element={<BookmarksRedirect />} />
                <Route
                  path="tracking"
                  element={
                    <PermissionGuard permission="module.project_management.view">
                      <TrackingBoardPage />
                    </PermissionGuard>
                  }
                />
                <Route
                  path="analytics"
                  element={
                    <PermissionGuard permission="module.procurements.analytics.read">
                      <AnalyticsLayout />
                    </PermissionGuard>
                  }
                >
                  <Route index element={<AnalyticsOverviewPage />} />
                  <Route path="pricing" element={<AnalyticsPricingPage />} />
                  {/* Former Lessons tab, merged into Pricing (audit P2-14). */}
                  <Route path="lessons" element={<Navigate to="../pricing" replace />} />
                  <Route path="market" element={<AnalyticsMarketPage />} />
                  <Route path="funnel" element={<AnalyticsFunnelPage />} />
                  <Route path="radar" element={<AnalyticsRadarPage />} />
                </Route>

              </Route>
              <Route path="tools" element={<ToolsModuleShell />}>
                <Route index element={<ToolsLandingPage />} />
                <Route path="document-comparison" element={<DocumentComparisonPage />} />
                <Route path="number-to-words" element={<NumberToWordsPage />} />
                <Route path="pdf-editor" element={<PdfEditorPage />} />
                <Route path="archive-manager" element={<ArchiveManagerPage />} />
                <Route path="arabic-keyboard" element={<ArabicKeyboardPage />} />
              </Route>
              <Route
                path="project-management"
                element={
                  <ModuleGuard moduleKey="project_management">
                    <PermissionGuard permission="module.project_management.view">
                      <ProjectManagementModuleShell />
                    </PermissionGuard>
                  </ModuleGuard>
                }
              >
                <Route index element={<ProjectManagementDashboardPage />} />
                <Route path="projects" element={<ProjectManagementProjectsPage />} />
                <Route path="planning" element={<GlobalPlanningPage />} />
                <Route path="projects/:projectId" element={<ProjectShell />}>
                  <Route index element={<TasksTab />} />
                  <Route path="tasks" element={<TasksTab />} />
                  <Route path="planning" element={<PlanningTab />} />
                  <Route path="members" element={<MembersTab />} />
                  <Route path="history" element={<HistoryTab />} />
                </Route>
                <Route path="taxonomy" element={<ProjectManagementTaxonomyPage />} />
                <Route path="settings" element={<ProjectManagementDashboardPage />} />
              </Route>
              <Route
                path="ai-chat"
                element={
                  <ModuleGuard moduleKey="ai_chat">
                    <PermissionGuard permission="module.ai_chat.view">
                      <AiChatModuleShell />
                    </PermissionGuard>
                  </ModuleGuard>
                }
              >
                <Route index element={<AiChatLandingPage />} />
                <Route path="discussions" element={<AiChatDiscussionsPage />} />
                <Route path="discussions/:conversationId" element={<AiChatConversationPage />} />
                <Route
                  path="governance"
                  element={
                    <PermissionGuard permission="module.ai_chat.govern">
                      <AiGovernancePage />
                    </PermissionGuard>
                  }
                />
                <Route
                  path="skills"
                  element={
                    <PermissionGuard permission="module.ai_chat.skills.manage">
                      <AiSkillsAdminPage />
                    </PermissionGuard>
                  }
                />
                <Route path="my-usage" element={<AiMyUsagePage />} />
              </Route>
              <Route
                path="letterheads"
                element={
                  <ModuleGuard moduleKey="letterheads">
                    <PermissionGuard permission="module.letterheads.view">
                      <LetterheadsModuleShell />
                    </PermissionGuard>
                  </ModuleGuard>
                }
              >
                <Route index element={<LetterheadLibraryPage />} />
                {/* static "letters" ranks above the editor's `:id` */}
                <Route path="letters" element={<LetterheadLettersPage />} />
                <Route path="letters/:letterId" element={<LetterheadLetterViewerPage />} />
                <Route path=":id" element={<LetterheadEditorPage />} />
              </Route>
              <Route
                path="tiers"
                element={
                  <ModuleGuard moduleKey="tiers">
                    <PermissionGuard permission="module.tiers.view">
                      <TiersModuleShell />
                    </PermissionGuard>
                  </ModuleGuard>
                }
              >
                <Route index element={<PartiesListPage />} />
                <Route path="new" element={<PartyCreatePage />} />
                <Route path="import" element={<PartyImportPage />} />
                <Route path=":id" element={<PartyDetailPage />} />
              </Route>
              <Route
                path="vault"
                element={
                  <ModuleGuard moduleKey="vault">
                    <PermissionGuard permission="module.vault.view">
                      <VaultModuleShell />
                    </PermissionGuard>
                  </ModuleGuard>
                }
              >
                {/* static "types" ranks above the detail route's `:documentId` */}
                <Route
                  path="types"
                  element={
                    <PermissionGuard permission="module.vault.manage">
                      <VaultTypesPage />
                    </PermissionGuard>
                  }
                />
                <Route index element={<VaultDashboardPage />} />
                <Route path=":documentId" element={<VaultDocumentDetailPage />} />
              </Route>
            </Route>

            {/* Public Shell */}
            <Route path="/" element={<Layout />}>
              <Route index element={<HomePage />} />
              <Route path="login" element={<LoginPage />} />
              <Route path="procurements" element={<ProcurementsPage />} />
              <Route path="auth/invitations/:token" element={<AcceptInvitationPage />} />
              <Route path="auth/magic/:token" element={<MagicLinkPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Routes>
        </Suspense>
        <PWAInstallPrompt />
      </OrgAuthProvider>
    </ErrorBoundary>
  )
}

export default App
